import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star, ShieldCheck, Truck, Zap, Clock, MapPin, Phone, Mail,
  Check, ArrowRight, Sparkles, Calendar, Send, Loader2, PenLine, X,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { LogoFull } from "@/components/Logo";
import { SERVICES, VEHICLE_SIZES, TRUST_BADGES, REVIEWS, FAQS, GALLERY, BUSINESS, HOURS, priceFor, priceFromLow } from "@/lib/data";
import { sendContact, fetchReviews, submitReview } from "@/lib/api";

const TRUST_ICON = { star: Star, "shield-check": ShieldCheck, truck: Truck, zap: Zap, clock: Clock };

// ---------- HERO ----------
const Hero = () => (
  <section id="home" className="relative min-h-[100svh] flex items-center hero-gradient overflow-hidden">
    {/* BG image */}
    <div className="absolute inset-0">
      <img
        src="/hero-bg.jpg"
        alt="Dillpickle's Carwash detailing a Jeep under the work tent"
        className="w-full h-full object-cover object-center opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E17] via-[#0A0E17]/85 to-[#0A0E17]/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-transparent to-[#0A0E17]/60" />
    </div>

    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full pt-28 pb-20 sm:pt-24 z-10">
      <div className="max-w-3xl">
        <Reveal>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#7CB342]/12 border border-[#7CB342]/30 mb-7">
            <span className="w-2 h-2 rounded-full bg-[#7CB342] animate-pulse" />
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#7CB342]">
              Booking now — Atlanta
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tighter text-[#F5F1E8]">
            Professional
            <br />
            Mobile Detail
            <br />
            <span className="text-[#7CB342] italic font-black">that comes to you.</span>
          </h1>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-7 text-lg sm:text-xl text-[#9CA3AF] max-w-xl leading-relaxed">
            Precision-crafted for the modern driver. We bring the luxury wash to your driveway
            in <span className="text-[#F5F1E8]">{BUSINESS.city}</span> — fully equipped, fully insured, fully obsessed with the finish.
          </p>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <Link
              to="/book"
              data-testid="hero-book-now-btn"
              className="btn-pickle group inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-base"
            >
              <Calendar size={18} strokeWidth={2.5} />
              Book My Detail
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#services"
              data-testid="hero-services-btn"
              className="btn-ghost-cream inline-flex items-center gap-2 rounded-full px-6 py-4 text-base"
            >
              See Services
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.35}>
          <div className="mt-12 flex items-center gap-5 text-sm text-[#9CA3AF]">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-[#7CB342] text-[#7CB342]" />)}
            </div>
            <span className="text-[#F5F1E8] font-medium">5.0</span>
            <span>·</span>
            <span>Loved across Atlanta</span>
          </div>
        </Reveal>
      </div>

      {/* Floating logo — splash-in entrance with water ripples */}
      <motion.div
        initial={{ opacity: 0, scale: 0.3, rotate: -18, y: 90 }}
        animate={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
        transition={{ type: "spring", stiffness: 110, damping: 11, mass: 1.1, delay: 0.35 }}
        className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2"
      >
        <motion.div
          animate={{ y: [0, -14, 0], rotate: [0, -1.5, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          <div className="absolute -inset-6 bg-[#2EA9E0]/20 blur-3xl rounded-full" />
          <div className="absolute inset-0 bg-[#7CB342]/25 blur-3xl rounded-full" />
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0.65, scale: 0.35 }}
              animate={{ opacity: 0, scale: 1.45 }}
              transition={{ delay: 0.6 + i * 0.22, duration: 1.15, ease: "easeOut" }}
              className="absolute inset-0 rounded-full border-2 border-[#2EA9E0]/60 pointer-events-none"
            />
          ))}
          <LogoFull size={460} className="relative drop-shadow-[0_25px_45px_rgba(0,0,0,0.6)]" />
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// ---------- TRUST MARQUEE ----------
const TrustBar = () => (
  <section className="relative py-10 border-y border-white/5 bg-[#0A0E17]" data-testid="trust-bar">
    <div className="overflow-hidden">
      <div className="flex marquee-track whitespace-nowrap gap-12 text-sm font-medium text-[#F5F1E8]/60">
        {[...Array(2)].flatMap((_, dup) =>
          TRUST_BADGES.map((b, i) => {
            const Icon = TRUST_ICON[b.icon] || Star;
            return (
              <span key={`${dup}-${i}`} className="inline-flex items-center gap-3">
                <Icon size={18} className="text-[#7CB342]" />
                <span>{b.title}</span>
                <span className="text-[#2EA9E0]/60">●</span>
              </span>
            );
          })
        )}
      </div>
    </div>
  </section>
);

// ---------- ABOUT ----------
const About = () => (
  <section id="about" className="section-pad bg-[#0A0E17]" data-testid="about-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div>
            <div className="label-eyebrow mb-4">About Us</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#F5F1E8]">
              From a pandemic idea to a <span className="text-[#7CB342]">passion</span>.
            </h2>
            <p className="mt-6 text-lg text-[#9CA3AF] leading-relaxed">
              Dillpickle's Carwash was founded during the COVID-19 pandemic with a simple goal:
              bring people together during a difficult time while providing a service that helped
              our community. What started as a small idea quickly grew into a passion for hard work,
              customer service, and building relationships with people from all walks of life.
            </p>
            <p className="mt-4 text-lg text-[#9CA3AF] leading-relaxed">
              Today, as a senior in high school, I continue to run Dillpickle's Carwash with the
              same values it was founded on — dedication, reliability, and attention to detail.
              Every vehicle is treated with care, and every customer is treated like a neighbor.
              As a fully mobile detailing service, I bring professional-quality car care directly
              to you, making it easy to keep your vehicle looking its best.
            </p>
            <p className="mt-4 text-lg text-[#9CA3AF] leading-relaxed">
              What began as a way to connect with the community during uncertain times has grown
              into a business built on trust, hard work, and a commitment to exceeding expectations
              with every wash.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#7CB342]/20 to-transparent rounded-3xl blur-2xl" />
            <img
              src="/about-detail.jpg"
              alt="Freshly detailed red Mustang by Dillpickle's Carwash"
              className="relative rounded-3xl border border-white/10 shadow-2xl w-full h-[500px] object-cover"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

// ---------- SERVICES ----------
const Services = () => (
  <section id="services" className="section-pad bg-[#0A0E17] relative" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mb-14">
        <Reveal>
          <div className="label-eyebrow mb-4">Services</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#F5F1E8]">
            Every wash, dialed in.
          </h2>
          <p className="mt-4 text-lg text-[#9CA3AF]">
            Four core packages — no upsells, no fluff. Choose what your car needs.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.05}>
            <div
              data-testid={`service-card-${s.id}`}
              className={`group relative rounded-2xl border ${s.popular ? "border-[#7CB342]/50" : "border-white/8"} bg-gradient-to-br ${s.accent} bg-[#111827] p-7 hover:-translate-y-1 hover:border-[#7CB342]/40 transition-all duration-300 h-full flex flex-col`}
            >
              {s.popular && (
                <span className="absolute -top-2.5 right-5 bg-[#E53935] text-white text-[10px] tracking-[0.2em] font-bold uppercase px-2.5 py-1 rounded-full">
                  Most Popular
                </span>
              )}
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} className="text-[#7CB342]" />
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">
                  {s.duration}
                </span>
              </div>
              <h3 className="font-display font-bold text-2xl text-[#F5F1E8] leading-tight">{s.name}</h3>
              <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{s.blurb}</p>

              <ul className="mt-5 space-y-2 flex-1">
                {s.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#F5F1E8]/80">
                    <Check size={15} className="mt-0.5 text-[#7CB342] flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-5 border-t border-white/5 flex items-end justify-between">
                <div>
                  <div className="text-xs text-[#9CA3AF]">Starting at</div>
                  <div className="font-display text-3xl font-extrabold text-[#7CB342]">${priceFromLow(s)}</div>
                </div>
                <Link
                  to={`/book?service=${s.id}`}
                  data-testid={`service-book-${s.id}`}
                  className="btn-pickle rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
                >
                  Book
                  <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ---------- PRICING ----------
const Pricing = () => (
  <section id="pricing" className="section-pad bg-[#0A0E17] relative border-t border-white/5" data-testid="pricing-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mb-14">
        <Reveal>
          <div className="label-eyebrow mb-4">Pricing</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#F5F1E8]">
            Honest pricing.
            <br />
            No hidden fees, ever.
          </h2>
          <p className="mt-4 text-lg text-[#9CA3AF]">
            Four packages. Three vehicle sizes. Pick yours below.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.06}>
            <div
              data-testid={`pricing-card-${s.id}`}
              className={`relative h-full rounded-3xl border ${
                s.popular ? "border-[#7CB342]/60 ring-1 ring-[#7CB342]/30" : "border-white/8"
              } bg-[#111827] p-7 flex flex-col transition-all duration-300 hover:-translate-y-1`}
            >
              {s.popular && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E53935] text-white text-[10px] tracking-[0.2em] font-bold uppercase px-3 py-1.5 rounded-full whitespace-nowrap shadow-lg"
                  data-testid={`pricing-popular-badge-${s.id}`}
                >
                  ★ Most Popular
                </span>
              )}

              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-[#7CB342]" />
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">
                  {s.duration}
                </span>
              </div>

              <h3 className="font-display font-bold text-xl text-[#F5F1E8] leading-tight">{s.name}</h3>
              <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{s.blurb}</p>

              <div className="mt-6 space-y-2.5">
                {VEHICLE_SIZES.map((v) => (
                  <div
                    key={v.id}
                    data-testid={`pricing-row-${s.id}-${v.id}`}
                    className="flex items-center justify-between rounded-xl bg-[#0A0E17] border border-white/5 px-4 py-3"
                  >
                    <div>
                      <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#9CA3AF]">
                        {v.label}
                      </div>
                    </div>
                    <div className="font-display font-extrabold text-2xl text-[#7CB342]">
                      ${priceFor(s, v.id)}
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to={`/book?service=${s.id}`}
                data-testid={`pricing-book-${s.id}`}
                className={`mt-6 inline-flex items-center justify-center gap-1.5 rounded-full py-3 text-sm ${
                  s.popular ? "btn-pickle" : "btn-ghost-cream"
                }`}
              >
                Book This Package
                <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[#9CA3AF]">
        <span>Heavy soiling / pet hair may incur a small add-on — quoted on arrival.</span>
        <span className="text-[#F5F1E8]/30">·</span>
        <span>No deposit required. Pay on completion.</span>
      </div>
    </div>
  </section>
);

// ---------- GALLERY ----------
const Gallery = () => (
  <section id="gallery" className="section-pad bg-[#0A0E17] border-t border-white/5" data-testid="gallery-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mb-14">
        <Reveal>
          <div className="label-eyebrow mb-4">Before &amp; After</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#F5F1E8]">
            See the receipts.
          </h2>
          <p className="mt-4 text-lg text-[#9CA3AF]">
            Drag the slider on each photo to reveal the transformation. Real Atlanta jobs.
          </p>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        {GALLERY.map((g, i) => (
          <Reveal key={i} delay={i * 0.07}>
            <div className="space-y-3" data-testid={`gallery-pair-${i}`}>
              <BeforeAfterSlider before={g.before} after={g.after} label={g.title} />
              <div className="flex items-center justify-between gap-3">
                <div className="font-display font-semibold text-[#F5F1E8]">{g.title}</div>
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#7CB342]">
                  {g.service}
                </span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ---------- REVIEWS ----------
const ReviewForm = ({ onClose, onSubmitted }) => {
  const [form, setForm] = useState({ name: "", location: "", rating: 5, text: "" });
  const [hoverRating, setHoverRating] = useState(0);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.text.trim().length < 10) {
      toast.error("Add your name and a quick note (10+ chars)");
      return;
    }
    setLoading(true);
    try {
      await submitReview(form);
      toast.success("Thanks! Your review is in — we'll publish it shortly.");
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error("Couldn't submit. Please text us at " + BUSINESS.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="review-modal-overlay"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="review-form"
        className="w-full sm:max-w-lg bg-[#111827] sm:rounded-3xl rounded-t-3xl border border-white/10 p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="label-eyebrow mb-2">Leave a Review</div>
            <h3 className="font-display font-bold text-2xl text-[#F5F1E8]">How'd we do?</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#9CA3AF] hover:text-[#F5F1E8] p-1"
            data-testid="review-modal-close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Your rating</label>
            <div className="mt-2 flex gap-1.5" onMouseLeave={() => setHoverRating(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHoverRating(n)}
                  onClick={() => setForm({ ...form, rating: n })}
                  data-testid={`review-star-${n}`}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    size={28}
                    className={`${(hoverRating || form.rating) >= n ? "fill-[#7CB342] text-[#7CB342]" : "text-[#9CA3AF]/40"} transition-colors`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Your name</label>
            <Input
              data-testid="review-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane D."
              className="mt-2 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8]"
            />
          </div>

          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Your review</label>
            <Textarea
              data-testid="review-text-input"
              rows={4}
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="Tell other drivers what made the experience great..."
              className="mt-2 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8]"
            />
          </div>

          <p className="text-xs text-[#9CA3AF] leading-relaxed">
            Reviews are moderated by Dylan before going live to keep things honest. Usually published within 24 hours.
          </p>

          <button
            type="submit"
            data-testid="review-submit-btn"
            disabled={loading}
            className="btn-pickle w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Sending..." : "Submit Review"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ReviewCard = ({ r, i }) => (
  <Reveal delay={i * 0.05}>
    <div
      data-testid={`review-card-${i}`}
      className="rounded-2xl border border-white/8 bg-[#111827] p-6 hover:border-[#7CB342]/30 transition h-full flex flex-col"
    >
      <div className="flex items-center gap-1 mb-4">
        {[...Array(r.rating)].map((_, j) => <Star key={j} size={14} className="fill-[#7CB342] text-[#7CB342]" />)}
      </div>
      <p className="text-[#F5F1E8]/85 text-sm leading-relaxed flex-1">"{r.text}"</p>
      <div className="mt-5 pt-5 border-t border-white/5">
        <div className="font-semibold text-[#F5F1E8] text-sm">{r.name}</div>
        {r.location && <div className="text-xs text-[#9CA3AF]">{r.location}</div>}
      </div>
    </div>
  </Reveal>
);

const Reviews = () => {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState([]);

  useEffect(() => {
    fetchReviews().then(setSubmitted).catch(() => {});
  }, []);

  const allReviews = [
    ...submitted.map((r) => ({ name: r.name, location: r.location, rating: r.rating, text: r.text })),
    ...REVIEWS,
  ];

  return (
    <section id="reviews" className="section-pad bg-[#0A0E17] border-t border-white/5" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <Reveal>
              <div className="label-eyebrow mb-4">Reviews</div>
              <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#F5F1E8]">
                5-star, every time.
              </h2>
              <div className="mt-5 flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => <Star key={i} size={20} className="fill-[#7CB342] text-[#7CB342]" />)}
                </div>
                <span className="text-[#F5F1E8] font-display text-xl font-bold">5.0</span>
                <span className="text-[#9CA3AF]">from Atlanta drivers</span>
              </div>
            </Reveal>
          </div>
          <Reveal>
            <button
              onClick={() => setOpen(true)}
              data-testid="leave-review-btn"
              className="btn-pickle inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm self-start"
            >
              <PenLine size={16} />
              Leave a Review
            </button>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {allReviews.map((r, i) => <ReviewCard key={i} r={r} i={i} />)}
        </div>

        {open && <ReviewForm onClose={() => setOpen(false)} onSubmitted={() => fetchReviews().then(setSubmitted).catch(() => {})} />}
      </div>
    </section>
  );
};

// ---------- FAQ ----------
const FAQ = () => (
  <section id="faq" className="section-pad bg-[#0A0E17] border-t border-white/5" data-testid="faq-section">
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="text-center mb-12">
          <div className="label-eyebrow mb-4">FAQ</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#F5F1E8]">
            Questions, answered.
          </h2>
        </div>
      </Reveal>

      <Reveal>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="rounded-2xl border border-white/8 bg-[#111827] px-6 data-[state=open]:border-[#7CB342]/40 transition-colors"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="text-left font-display font-semibold text-lg text-[#F5F1E8] hover:no-underline hover:text-[#7CB342] py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#9CA3AF] leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

// ---------- CONTACT ----------
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast.success("Message sent — we'll get back to you fast.");
      setForm({ name: "", email: "", message: "" });
    } catch (e) {
      toast.error("Something went wrong. Try texting us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-pad bg-[#0A0E17] border-t border-white/5" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div>
              <div className="label-eyebrow mb-4">Contact</div>
              <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#F5F1E8]">
                Let's talk shine.
              </h2>
              <p className="mt-5 text-lg text-[#9CA3AF]">
                Questions, custom requests, or fleet pricing? Fastest reply is a text.
              </p>

              <div className="mt-8 space-y-4">
                <a href={BUSINESS.phoneHref} className="flex items-center gap-4 group" data-testid="contact-phone-link">
                  <div className="w-12 h-12 rounded-full bg-[#7CB342]/15 border border-[#7CB342]/30 flex items-center justify-center group-hover:bg-[#7CB342] group-hover:text-[#0A0E17] transition">
                    <Phone size={18} className="text-[#7CB342] group-hover:text-[#0A0E17]" />
                  </div>
                  <div>
                    <div className="text-xs text-[#9CA3AF] tracking-wide">Call or text</div>
                    <div className="font-display font-semibold text-lg text-[#F5F1E8] group-hover:text-[#7CB342] transition">{BUSINESS.phone}</div>
                  </div>
                </a>
                <a href={BUSINESS.emailHref} className="flex items-center gap-4 group" data-testid="contact-email-link">
                  <div className="w-12 h-12 rounded-full bg-[#7CB342]/15 border border-[#7CB342]/30 flex items-center justify-center group-hover:bg-[#7CB342] transition">
                    <Mail size={18} className="text-[#7CB342] group-hover:text-[#0A0E17]" />
                  </div>
                  <div>
                    <div className="text-xs text-[#9CA3AF] tracking-wide">Email</div>
                    <div className="font-display font-semibold text-lg text-[#F5F1E8] group-hover:text-[#7CB342] transition break-all">{BUSINESS.email}</div>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#7CB342]/15 border border-[#7CB342]/30 flex items-center justify-center">
                    <MapPin size={18} className="text-[#7CB342]" />
                  </div>
                  <div>
                    <div className="text-xs text-[#9CA3AF] tracking-wide">Service area</div>
                    <div className="font-display font-semibold text-lg text-[#F5F1E8]">Atlanta, GA · Fully mobile</div>
                  </div>
                </div>
              </div>

              {/* Hours & Availability */}
              <div className="mt-8 rounded-2xl border border-white/8 bg-[#111827] p-6" data-testid="hours-card">
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={16} className="text-[#7CB342]" />
                  <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#7CB342]">
                    Summer Hours
                  </span>
                </div>
                <ul className="divide-y divide-white/5">
                  {HOURS.map((h) => (
                    <li key={h.day} className="flex items-center justify-between py-2.5">
                      <span className="text-sm text-[#F5F1E8]/85 font-medium">{h.day}</span>
                      <span className={`text-sm font-semibold ${h.closed ? "text-[#E53935]/90" : "text-[#7CB342]"}`}>
                        {h.closed ? "Closed" : h.range}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 rounded-xl border border-[#7CB342]/25 bg-[#7CB342]/8 p-4 text-sm text-[#F5F1E8]/85 leading-relaxed" data-testid="availability-note">
                  <strong className="text-[#7CB342]">Limited availability.</strong>{" "}
                  Dillpickle's is a one-person mobile detailing business operated by a high school
                  student. To ensure every vehicle gets the highest level of care and attention,
                  <strong className="text-[#F5F1E8]"> only one vehicle is scheduled per day</strong>.
                  Please book in advance — dates fill up fast.
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-white/8 bg-[#111827] p-7 sm:p-9 space-y-5"
            >
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Name</label>
                <Input
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="mt-2 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8]"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Email</label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                  className="mt-2 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8]"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">Message</label>
                <Textarea
                  data-testid="contact-message-input"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="What kind of detail are you thinking?"
                  className="mt-2 bg-[#0A0E17] border-white/10 focus-visible:border-[#7CB342] focus-visible:ring-[#7CB342]/30 text-[#F5F1E8]"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="btn-pickle w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ---------- TRUST GRID (between Pricing and Gallery) ----------
const TrustGrid = () => (
  <section className="py-20 bg-[#0A0E17] border-t border-white/5" data-testid="trust-grid">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {TRUST_BADGES.map((b, i) => {
          const Icon = TRUST_ICON[b.icon] || Star;
          return (
            <Reveal key={b.title} delay={i * 0.05}>
              <div className="rounded-2xl border border-white/8 bg-[#111827] p-5 text-center hover:border-[#7CB342]/40 hover:-translate-y-1 transition" data-testid={`trust-badge-${i}`}>
                <div className="inline-flex w-11 h-11 rounded-full bg-[#7CB342]/15 items-center justify-center mb-3">
                  <Icon size={20} className="text-[#7CB342]" />
                </div>
                <div className="font-display font-bold text-sm text-[#F5F1E8]">{b.title}</div>
                <div className="text-xs text-[#9CA3AF] mt-1">{b.desc}</div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

// ---------- CTA ----------
const FinalCTA = () => (
  <section className="section-pad bg-[#0A0E17] border-t border-white/5" data-testid="final-cta">
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="relative rounded-[2rem] overflow-hidden p-10 sm:p-16 text-center border border-[#7CB342]/30 bg-gradient-to-br from-[#7CB342]/15 via-[#111827] to-[#111827]">
          <div className="absolute inset-0 opacity-20">
            <img src="/cta-bg.jpg" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative">
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#F5F1E8]">
              Ready for a car that looks
              <br /> <span className="text-[#7CB342]">brand new again?</span>
            </h2>
            <p className="mt-5 text-lg text-[#F5F1E8]/70 max-w-xl mx-auto">
              Book in under 90 seconds. We come to you. Atlanta metro only — one vehicle per day, so book early.
            </p>
            <Link
              to="/book"
              data-testid="cta-book-btn"
              className="btn-pickle inline-flex items-center gap-2.5 mt-8 rounded-full px-8 py-4 text-base"
            >
              <Calendar size={18} strokeWidth={2.5} />
              Book My Detail
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <main data-testid="home-page">
      <Hero />
      <TrustBar />
      <About />
      <Services />
      <Pricing />
      <TrustGrid />
      <Gallery />
      <Reviews />
      <FAQ />
      <FinalCTA />
      <Contact />
    </main>
  );
}
